import { RefreshCw } from 'lucide-react'
import { useElapsedSince } from '../../../shared/hooks/useElapsedSince'
import {
  formatScheduleAge,
  formatScheduleAgeSpoken,
  freshnessLabel,
  resolveFreshnessLevel,
  type ScheduleFreshnessLevel,
} from '../model/dataFreshness'

/** Dot colour is the badge's whole payload, so it is the one thing indexed rather than branched. */
const DOT_CLASS: Record<ScheduleFreshnessLevel, string> = {
  unknown: 'bg-gray-300',
  fresh: 'bg-green-500',
  aging: 'bg-amber-500',
  stale: 'bg-red-500',
  disconnected: 'bg-red-500',
}

const TEXT_CLASS: Record<ScheduleFreshnessLevel, string> = {
  unknown: 'text-gray-500',
  fresh: 'text-gray-500',
  aging: 'text-amber-700',
  stale: 'text-red-700',
  disconnected: 'text-red-700',
}

interface Props {
  /**
   * Epoch ms of the oldest data on screen, or `null` before the first success.
   *
   * A timestamp, deliberately, not a pre-computed `secondsAgo`. An age prop would change every
   * tick and drag every ancestor that passes it into the re-render; a timestamp changes only when
   * a query settles, and the clock stays inside this component.
   */
  lastUpdatedAt: number | null
  isFetching: boolean
  isDisconnected: boolean
  onRefresh: () => void
  className?: string
}

/**
 * How old the calendar on screen is, and the button that fixes it.
 *
 * **Quiet when it has nothing to say.** On a phone the age text is hidden while the data is fresh
 * and only the refresh button remains: the header it sits in has to survive 320px next to `Heute`
 * and two arrows, and "everything is fine" does not deserve that space. From `sm` up there is room,
 * so the full pill shows always. The rule is CSS, not `useIsMobile()` — that hook is the 768px
 * breakpoint and re-renders on every resize event at the top of the calendar grid.
 *
 * Unlike the work-order badge this is modelled on, the dot is not hardcoded green. That one reads
 * "Verbunden" with the server down; here the colour is the information, and `Keine Verbindung`
 * keeps the age beside it because "a request failed" without "and this is nine minutes old" is not
 * something a worker can act on.
 */
export const ScheduleFreshnessBadge = ({
  lastUpdatedAt,
  isFetching,
  isDisconnected,
  onRefresh,
  className,
}: Props) => {
  const ageMs = useElapsedSince(lastUpdatedAt)
  const level = resolveFreshnessLevel(ageMs, isDisconnected)
  const age = formatScheduleAge(ageMs)

  // `unknown` is quiet too: on a healthy cold start it lasts under a second, and a first-load
  // failure lands in `disconnected`, which is loud.
  const isQuiet = level === 'fresh' || level === 'unknown'

  return (
    <div className={`flex shrink-0 items-center gap-1.5 ${className ?? ''}`}>
      <span
        className={`items-center gap-1.5 ${isQuiet ? 'hidden sm:flex' : 'flex'}`}
        // One string for a screen reader, so the dot's colour is never the only carrier. Coarser
        // than the text beside it on purpose: this is a polite live region, and an age that
        // changed every second would be re-announced every second.
        aria-label={`${freshnessLabel(level)}. ${formatScheduleAgeSpoken(ageMs)}.`}
        role="status"
      >
        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${DOT_CLASS[level]}`} aria-hidden />
        {isDisconnected && (
          <span className="whitespace-nowrap text-[13px] font-semibold text-red-700" aria-hidden>
            Keine Verbindung
          </span>
        )}
        <span
          className={`whitespace-nowrap text-[13px] ${TEXT_CLASS[level]}`}
          aria-hidden
        >
          {age}
        </span>
      </span>

      <button
        type="button"
        onClick={onRefresh}
        disabled={isFetching}
        title="Aktualisieren"
        aria-label="Aktualisieren"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[#124093] transition hover:bg-gray-100 active:bg-gray-100 disabled:opacity-50"
      >
        <RefreshCw
          className={`h-[18px] w-[18px] ${isFetching ? 'animate-spin' : ''}`}
          strokeWidth={2.5}
          aria-hidden
        />
      </button>
    </div>
  )
}
